import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../store/store";
import { setSelectedStation } from "../../../store/orm/userSession";
import ORMfunctions from "../../../hooks/app/general";
import { useKeydownListener } from "../../../hooks/useKeydownListener";
import { useClickOutside } from "../../../hooks/useClickOutside";

import { Station } from "../../../store/orm/stations";
import { getStationById } from "../../../store/orm/stations";

interface StationselectProps {
  type: "start" | "end";
  useapi?: boolean;
  regioncodes?: string[];
  rangelimit?: number;
  resultlimit?: number;
  viewType?: string;
  readonly?: boolean;
  labelId?: string;
  enabledstations?: string;
}

export const Stationselect: React.FC<StationselectProps> = ({
  type,
  useapi = false,
  regioncodes = ["DE"],
  rangelimit = 50,
  resultlimit = 10,
  viewType = "",
  readonly = false,
  labelId,
  enabledstations = null,
}) => {
  const dispatch = useDispatch();

  let stations = [] as Station[];
  const triggerRef = useRef<HTMLDivElement>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const [showSelect, setShowSelect] = useState(false);
  const [showSelectEnd, setShowSelectEnd] = useState(false);
  const [readonlyReact, setReadonlyReact] = useState(readonly);

  // Get station data from Redux store
  let startStation = useSelector(
    (state: RootState) => state.userSession?.selectedStation,
  );

  const endStation = useSelector(
    (state: RootState) => state.userSession?.selectedStationEnd,
  );

  if (!enabledstations) {
    stations = useSelector(
      (state: RootState) => state.stations.stations,
    ) as Station[];
  } else {
    const allstations = useSelector(
      (state: RootState) => state.stations.stations,
    ) as Station[];

    stations = allstations.filter((item) => enabledstations.includes(item.id));

    if (!stations.find((element) => element.id === startStation)) {
      // startStation = stations[0].id;
      if (stations[0])
        dispatch(
          setSelectedStation({ type: "start", stationId: stations[0].id }),
        );
    }
  }

  const stationSelectedID = type === "start" ? startStation! : endStation!;

  const stationSelected = useSelector((state: RootState) =>
    getStationById(state, stationSelectedID),
  );

  useEffect(() => {
    if (stations.length <= 1) {
      setReadonlyReact(true);
    } else {
      setReadonlyReact(false);
    }
  }, [stations]);

  useEffect(() => {
    if (startStation === endStation) {
      setShowSelectEnd(false);
    } else {
      setShowSelectEnd(!!stationSelected);
    }
  }, [startStation, endStation, stationSelected]);

  // const [stationSelected, setStationSelected] = useState<any>({});

  // Handle opening the select
  const handleOpen = useCallback(
    (event: React.MouseEvent | KeyboardEvent) => {
      if (readonlyReact) return;
      console.log("event", event);

      if (event.type === "keydown") {
        const kbEvent = event as KeyboardEvent;
        if (!showSelect) {
          switch (kbEvent.key) {
            case "Enter":
            case "ArrowUp":
            case "ArrowDown":
              if (!showSelect) {
                setShowSelect(true);
              }
              if (type === "end") {
                setShowSelectEnd(true);
                setShowSelect(true);
              }

              kbEvent.preventDefault();
              break;
            case "Escape":
              setShowSelect(false);
              break;
            case "Tab":
            case "Shift":
              break;
            default:
              kbEvent.preventDefault();
              break;
          }
        } else {
          let currentElement = document.getElementById(
            `select-${stationSelectedID}`,
          ) as HTMLElement;

          switch (kbEvent.key) {
            case "ArrowUp":
            case "ArrowDown":
              event.preventDefault();

              if (!currentElement) {
                const first = triggerRef.current?.querySelector(
                  ".orm-stationselect__option--search",
                ) as HTMLElement;
                // console.log("currentElement", first);
                currentElement = first;
              } else {
                currentElement = (
                  kbEvent.key === "ArrowUp"
                    ? currentElement.previousElementSibling
                    : currentElement.nextElementSibling
                ) as HTMLElement;
              }

              if (
                currentElement &&
                currentElement.hasAttribute("aria-selected")
              ) {
                const value = currentElement.getAttribute("data-value");
                if (value) {
                  // handleStationSelect(value);
                  // currentElement.focus();
                  currentElement.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                  });
                }
              }
              break;
            case "Enter":
              if (stationSelectedID) {
                handleStationSelect(stationSelectedID);
              }
              break;
            case "Escape":
              // inputRef.current?.focus();
              // handleClear();
              handleClose();
              break;
            case "Tab":
              // setFirstInit(false);
              handleClose();
              break;
            default:
              // inputRef.current?.focus();
              break;
          }
        }
      } else {
        setShowSelectEnd(true);
        setShowSelect(true);
      }
    },
    [readonlyReact, showSelect, type],
  );

  // Handle closing the select
  const handleClose = useCallback(() => {
    setShowSelect(false);
  }, []);

  // Handle station selection
  const handleStationSelect = useCallback(
    (stationId: string) => {
      // console.log("setstation", stationId);

      if (!stationId) return;

      if (stationId === "startend") {
        setShowSelect(false);
        setShowSelectEnd(false);
        if (type === "end") {
          dispatch(setSelectedStation({ type: "end", station: null }));
        }
        return;
      }

      setShowSelect(false);

      if (stationSelectedID === stationId) return;

      if (type === "start") {
        dispatch(setSelectedStation({ type: "start", stationId }));
      } else {
        if (startStation! === stationId) {
          dispatch(setSelectedStation({ type: "end", stationId: null }));
          setShowSelectEnd(false);
          return;
        } else {
          dispatch(setSelectedStation({ type: "end", stationId }));
          setShowSelectEnd(true);
        }
      }
    },
    [dispatch, type, stationSelected, startStation, stations],
  );

  // Keyboard listener for trigger element
  const handleKeydown = useCallback(
    (event: KeyboardEvent) => {
      handleOpen(event);
    },
    [handleOpen],
  );

  useKeydownListener(triggerRef, handleKeydown);

  //
  useClickOutside(containerRef, handleClose);
  // Render unselected end station
  if (type === "end" && !showSelectEnd) {
    return (
      <div
        className={`orm-stationselect__endstation__unselected ${
          viewType === "mapModule" ? "orm-lmap" : ""
        }`}
        id="stationselect-trigger"
        aria-expanded={showSelect}
        aria-controls="stationselect-listbox"
        aria-activedescendant={
          stationSelectedID ? `station-${stationSelectedID}` : ""
        }
        aria-labelledby={labelId}
        role="combobox"
        aria-autocomplete="list"
        tabIndex={readonlyReact ? -1 : 0}
        onClick={handleOpen}
        ref={triggerRef}
      >
        <div className="orm-fakecheck"></div>
        <p className="orm-stationselect__label">
          {ORMfunctions.i18n("general/returnStation") || "Rückgabestation"}
        </p>
      </div>
    );
  }

  return (
    <div className="orm-stationselect text-left" ref={containerRef}>
      <div>
        <div
          className="orm-stationselect__select"
          title={
            stationSelected
              ? `${stationSelected.name} - ${stationSelected.street}, ${stationSelected.city}`
              : ORMfunctions.i18n("general/chooseStation") || "Station wählen"
          }
          id="stationselect-trigger-start"
          aria-expanded={showSelect}
          aria-controls="stationselect-listbox"
          aria-activedescendant={
            stationSelectedID ? `station-${stationSelectedID}` : ""
          }
          aria-labelledby={labelId}
          role="combobox"
          aria-autocomplete="list"
          onClick={() => setShowSelect(true)}
          tabIndex={readonlyReact ? -1 : 0}
          ref={triggerRef}
        >
          {stationSelected ? (
            <div>
              <div>
                <strong>{stationSelected.name}</strong>
              </div>
              <small className="noBreak overflowHidden textTrim">
                {stationSelected.street}, {stationSelected.city}
              </small>
            </div>
          ) : (
            <div>
              <strong>
                {" "}
                {ORMfunctions.i18n("general/chooseStation") || "Station wählen"}
              </strong>
            </div>
          )}
          <span className="orm-station__address__icon">
            <i className="fas fa-map-marker-alt"></i>
          </span>
        </div>
        {showSelect ? (
          <div
            className="orm-stationselect__options"
            role="listbox"
            tabIndex={0}
            aria-labelledby={labelId}
            id="stationselect-listbox"
          >
            {type === "end" && (
              <div
                className="orm-stationselect__option orm-stationselect__option--search"
                onClick={() => handleStationSelect("startend")}
                role="option"
                tabIndex={-1}
                aria-selected={stationSelectedID === "startend"}
                id="select-startend"
              >
                {ORMfunctions.i18n("general/likeStartstation") ||
                  "Wie Startstation"}
              </div>
            )}
            {stations.map((station: any) => (
              <div
                className={
                  stationSelected
                    ? stationSelected.id == station.id
                      ? "orm-stationselect__option selected"
                      : "orm-stationselect__option"
                    : "orm-stationselect__option"
                }
                data-value={station.id}
                key={station.id}
                data-selected={
                  stationSelected ? stationSelected.id == station.id : false
                }
                onClick={() => {
                  const stationId = station.id;
                  handleStationSelect(stationId);
                  setShowSelect(false);
                }}
                role="option"
                tabIndex={-1}
                aria-selected={stationSelected === station.id}
                id={`select-${station.id}`}
                title={`${station.name} - ${station.street} , ${station.zipcode} ${station.city}`}
              >
                <div>
                  <strong>{station.name}</strong>
                </div>
                <small className="noBreak overflowHidden textTrim">
                  {station.street}, {station.city}
                </small>
              </div>
            ))}
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};
